import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * WHYSHIFT collection list with explicit Load More button (no infinite scroll).
 *
 * @typedef {import('@theme/paginated-list').default} PaginatedListBase
 * @typedef {object} WhyshiftRefs
 * @property {HTMLButtonElement} [loadMoreButton]
 * @property {HTMLElement} [loadMoreWrapper]
 * @property {HTMLElement} [loadMoreCount]
 */

class WhyshiftCollectionList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    this.#updateLoadMoreButton();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#syncAfterFilterUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#syncAfterFilterUpdate);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateLoadMoreButton();
  }

  #syncAfterFilterUpdate = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const initialLastPage = grid.dataset.lastPage;
    const initialTotal = grid.dataset.totalItems;

    const observer = new MutationObserver(() => {
      const lastPage = grid.dataset.lastPage;
      const total = grid.dataset.totalItems;

      if (lastPage !== initialLastPage || total !== initialTotal) {
        observer.disconnect();
        if (this.isConnected) this.#updateLoadMoreButton();
      }
    });

    observer.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page', 'data-total-items'],
      childList: true,
    });

    setTimeout(() => {
      observer.disconnect();
      if (this.isConnected) this.#updateLoadMoreButton();
    }, 500);
  };

  /**
   * @param {Event} [event]
   */
  async loadMore(event) {
    event?.preventDefault();

    const { loadMoreButton } = this.refs;
    if (loadMoreButton?.disabled) return;

    if (loadMoreButton) {
      loadMoreButton.disabled = true;
      loadMoreButton.setAttribute('aria-busy', 'true');
    }

    await this.#appendNextPage();

    if (loadMoreButton) {
      loadMoreButton.disabled = false;
      loadMoreButton.removeAttribute('aria-busy');
    }

    this.#updateLoadMoreButton();
  }

  async #appendNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getNextPageInfo();
    if (!nextPage || !this.#shouldUsePageInfo(nextPage)) return;

    let nextPageItemElements = this.#getGridItemsForPage(nextPage.page);

    if (!nextPageItemElements) {
      await this.#fetchPageByNumber(nextPage.page, nextPage.url);
      nextPageItemElements = this.#getGridItemsForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      const prefetch = this.#getNextPageInfo();
      if (prefetch && this.#shouldUsePageInfo(prefetch)) {
        this.#fetchPageByNumber(prefetch.page, prefetch.url);
      }
    });
  }

  /**
   * @param {{ page: number, url?: URL }} pageInfo
   */
  #shouldUsePageInfo(pageInfo) {
    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPageInfo() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;

    const targetCard = cards[cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #fetchPageByNumber(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePageInfo(pageInfo)) return;

    const { sectionRenderer } = await import('@theme/section-renderer');
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridItemsForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  #updateLoadMoreButton() {
    const { grid, loadMoreButton, loadMoreCount, loadMoreWrapper } = this.refs;
    if (!grid || !loadMoreWrapper) return;

    const cards = this.refs.cards;
    const loaded = Array.isArray(cards) ? cards.length : 0;
    const total = Number(grid.dataset.totalItems || loaded);
    const lastPage = Number(grid.dataset.lastPage || 1);
    const lastCard = Array.isArray(cards) ? cards[cards.length - 1] : null;
    const currentPage = lastCard ? Number(lastCard.dataset.page) : 1;

    if (loadMoreCount) {
      loadMoreCount.textContent = `${loaded} of ${total}`;
    }

    const hasMore = currentPage < lastPage;

    loadMoreWrapper.classList.toggle('is-hidden', !hasMore);

    if (loadMoreButton) {
      loadMoreButton.disabled = !hasMore;
    }
  }
}

if (!customElements.get('whyshift-collection-list')) {
  customElements.define('whyshift-collection-list', WhyshiftCollectionList);
}
